import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Checkbox, Input, Radio, Modal, notification, Spin } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Button as CrceButton } from '@crc-evans/ui-components';
import { Loader2, Plus, Save, ShieldAlert, Trash2 } from 'lucide-react';
import moment from 'moment';
import AddIssueModal from './AddIssueModal';
import {
  fetchEhsLog,
  updateEhsLog,
  addEhsLogItem,
  deleteEhsLogItem,
  fetchAllEhsIssues,
  updateDsrLogSections
} from '../../redux/dsr/action';
import './TechnicianLogSection.css';
import './EHSSection.css';

/** API may return { data: [...] }, nested data, or a bare array. */
function normalizeEhsLogList(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
}

function mapSeverityFromApi(item) {
  const major = Number(item?.major) === 1;
  const minor = Number(item?.minor) === 1;
  if (major) return 'major';
  if (minor) return 'minor';
  return null;
}

function mapIssueFromApi(item) {
  const severity = mapSeverityFromApi(item);
  return {
    ...item,
    id: item.id != null ? Number(item.id) : item.id,
    severity,
    major: severity === 'major' ? 1 : 0,
    minor: severity === 'minor' ? 1 : 0,
  };
}

const EHSSection = ({
  shift,
  selectedDate,
  onComplete,
  onCancel,
  selectedProject,
  selectedMainline,
  currentDsrLog,
  isSkipped,
  isSubmitted,
  isShiftSkipped = false,
  onSkipChange,
  currentEhsLog,
  allEhsIssues,
  isEhsLogLoading,
  isUpdatingEhsLog,
  isAddingEhsLogItem,
  onFetchEhsLog,
  onUpdateEhsLog,
  onAddEhsLogItem,
  onDeleteEhsLogItem,
  onFetchAllEhsIssues,
  onUpdateDsrLogSections,
}) => {
  const [skipped, setSkipped] = useState(false);
  const [issues, setIssues] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [hoveredRowId, setHoveredRowId] = useState(null);

  const getShiftNumber = (shiftStr) => (shiftStr === 'day' ? 0 : 1);

  useEffect(() => {
    if (selectedProject && selectedMainline && selectedDate && !isSkipped) {
      const userEmail = JSON.parse(localStorage.getItem('user')).email;
      const logDate = moment(selectedDate).format('YYYY-MM-DD');
      const shiftNumber = getShiftNumber(shift);
      onFetchEhsLog({
        projectId: selectedProject.id,
        logDate,
        userEmail,
        shift: shiftNumber,
        sowId: selectedMainline.id,
      });
      onFetchAllEhsIssues();
    }
  }, [selectedProject, selectedMainline, selectedDate, shift, isSkipped, onFetchEhsLog, onFetchAllEhsIssues]);

  useEffect(() => {
    if (currentEhsLog == null) return;
    const list = normalizeEhsLogList(currentEhsLog);
    const mapped = list.map(mapIssueFromApi);
    setIssues(mapped);
    setSelectedRowKeys(
      mapped
        .filter(
          (item) =>
            item.severity ||
            (item.comments != null && String(item.comments).trim() !== '')
        )
        .map((item) => item.id)
    );
    setIsDirty(false);
  }, [currentEhsLog]);

  useEffect(() => {
    setSkipped(isSkipped || false);
  }, [isSkipped]);

  useEffect(() => {
    setIsDirty(false);
  }, [selectedDate]);

  useEffect(() => {
    setSkipped(isSkipped || false);
  }, [isSkipped, shift, selectedDate]);

  useEffect(() => {
    return () => {
      setIssues([]);
      setSkipped(false);
      setIsDirty(false);
      setIsAddModalVisible(false);
      setSelectedRowKeys([]);
    };
  }, []);

  const handleIssueChange = (id, field, value) => {
    setIssues((prevIssues) =>
      prevIssues.map((item) => {
        if (item.id !== id) return item;
        if (field === 'severity') {
          return {
            ...item,
            severity: value,
            major: value === 'major' ? 1 : 0,
            minor: value === 'minor' ? 1 : 0,
          };
        }
        return { ...item, [field]: value };
      })
    );
    setIsDirty(false);
  };

  const handleAddIssue = (ehsIssueId) => {
    onAddEhsLogItem({
      ehsIssueId,
      projectId: selectedProject.id,
      dsrLogId: currentDsrLog.id,
      shift: getShiftNumber(shift),
      logDate: moment(selectedDate).format('YYYY-MM-DD'),
      userEmail: JSON.parse(localStorage.getItem('user')).email,
      sowId: selectedMainline.id,
    });
    setIsAddModalVisible(false);
  };

  const handleDeleteSelected = () => {
    selectedRowKeys.forEach((id) => {
      onDeleteEhsLogItem({
        id,
        projectId: selectedProject.id,
        logDate: moment(selectedDate).format('YYYY-MM-DD'),
        userEmail: JSON.parse(localStorage.getItem('user')).email,
        shift: getShiftNumber(shift),
        sowId: selectedMainline.id,
      });
    });
    setSelectedRowKeys([]);
  };

  const generateSubmissionStatus = (section, sh, isSubmittedFlag = true) => {
    const shiftKeySuffix = sh === 'day' ? 'Dayshift' : 'Nightshift';
    let submittedKey;
    switch (section) {
      case 'technician':
        submittedKey = `isTechnicianLogSubmitted${shiftKeySuffix}`;
        break;
      case 'downtime':
        submittedKey = `isDowntimeSubmitted${shiftKeySuffix}`;
        break;
      case 'consumables':
        submittedKey = `isConsumablesSubmitted${shiftKeySuffix}`;
        break;
      case 'equipment':
        submittedKey = `isEquipmentUsageSubmitted${shiftKeySuffix}`;
        break;
      case 'ehs':
        submittedKey = `isEhsIssuesSubmitted${shiftKeySuffix}`;
        break;
      case 'material':
        submittedKey = `isMaterialUsageSubmitted${shiftKeySuffix}`;
        break;
      case 'production':
        submittedKey = `isProductionJointSubmitted${shiftKeySuffix}`;
        break;
      default:
        return {};
    }
    return { [submittedKey]: isSubmittedFlag };
  };

  const handleSave = async () => {
    if (skipped) {
      if (onComplete) onComplete(true, 'ehs');
      return;
    }

    const selectedIds = selectedRowKeys.map((k) => Number(k));
    const dsrLogEhsIssues = issues
      .filter(
        (item) =>
          selectedIds.includes(Number(item.id)) || item.severity || item.comments
      )
      .map((item) => ({
        id: item.id,
        major: item.severity === 'major' ? 1 : 0,
        minor: item.severity === 'minor' ? 1 : 0,
        comments: item.comments || '',
        dsrLogId: currentDsrLog?.id,
        shift: getShiftNumber(shift),
      }));

    const payload = {
      dsrLogEhsIssues,
      projectId: selectedProject.id,
      logDate: moment(selectedDate).format('YYYY-MM-DD'),
      userEmail: JSON.parse(localStorage.getItem('user')).email,
      sowId: selectedMainline.id,
      shift: getShiftNumber(shift),
    };

    try {
      await onUpdateEhsLog(payload);
      setIsDirty(false);
      const submissionStatus = generateSubmissionStatus('ehs', shift, true);
      const sectionsSkipped = {
        ...currentDsrLog.sectionsSkipped,
        isEhsSkippedDayShift: shift === 'day' ? 0 : (currentDsrLog.sectionsSkipped?.isEhsSkippedDayShift || 0),
        isEhsSkippedNightShift: shift === 'night' ? 0 : (currentDsrLog.sectionsSkipped?.isEhsSkippedNightShift || 0),
      };
      await onUpdateDsrLogSections({
        dsrLogId: currentDsrLog?.id,
        submissionStatus: submissionStatus,
        sectionsSkipped: sectionsSkipped,
      });
      if (onComplete) onComplete(true, 'ehs');
      setTimeout(() => {
        if (selectedProject && selectedMainline && selectedDate) {
          const userEmail = JSON.parse(localStorage.getItem('user')).email;
          const logDate = moment(selectedDate).format('YYYY-MM-DD');
          const shiftNumber = shift === 'day' ? '0' : '1';
          onFetchEhsLog({
            projectId: selectedProject.id,
            logDate: logDate,
            userEmail: userEmail,
            shift: shiftNumber,
            sowId: selectedMainline.id,
          });
        }
      }, 100);
    } catch (error) {
      console.error('Error saving EHS log:', error);
      notification.error({
        message: 'Save Failed',
        description: 'Failed to save EHS data. Please try again.',
      });
    }
  };

  const handleSkipChange = (e) => {
    const newSkipState = e.target.checked;
    setSkipped(newSkipState);
    setIsDirty(true);
    if (onSkipChange) {
      onSkipChange(e);
    } else if (currentDsrLog?.id) {
      const submissionStatus = generateSubmissionStatus('ehs', shift, true);
      const sectionsSkipped = {
        ...currentDsrLog.sectionsSkipped,
        isEhsSkippedDayShift: shift === 'day' ? (newSkipState ? 1 : 0) : (currentDsrLog.sectionsSkipped?.isEhsSkippedDayShift || 0),
        isEhsSkippedNightShift: shift === 'night' ? (newSkipState ? 1 : 0) : (currentDsrLog.sectionsSkipped?.isEhsSkippedNightShift || 0),
      };
      onUpdateDsrLogSections({
        dsrLogId: currentDsrLog.id,
        submissionStatus: submissionStatus,
        sectionsSkipped: sectionsSkipped,
      });
    }
  };
  const handleNext = () => {
    if (isDirty) {
      Modal.confirm({
        title: 'Warning!',
        icon: <ExclamationCircleOutlined />,
        content: 'You have made changes. Do you want to save them?',
        okText: 'Save',
        cancelText: 'Discard',
        onOk: () => {
          handleSave();
          if (onComplete) onComplete(!skipped);
        },
        onCancel: () => {
          if (onComplete) onComplete(!skipped);
        },
      });
    } else {
      if (onComplete) onComplete(!skipped);
    }
  };
  const toolbarDisabled = skipped || isSubmitted;
  const deleteDisabled =
    selectedRowKeys.length === 0 || skipped || isSubmitted;

  const toggleRowSelected = (rowId, checked) => {
    const id = Number(rowId);
    setSelectedRowKeys((prev) => {
      const prevNums = prev.map((k) => Number(k));
      if (checked) {
        return prevNums.includes(id) ? prevNums : [...prevNums, id];
      }
      return prevNums.filter((k) => k !== id);
    });
  };

  const isRowSelected = (rowId) => selectedRowKeys.map((k) => Number(k)).includes(Number(rowId));

  return (
    <div className="modern-dsr-tech-log-section modern-dsr-ehs-usage">
      <Spin
        spinning={isEhsLogLoading}
        indicator={(
          <span className="modern-dsr-tech-log-spin" aria-hidden>
            <Loader2 className="modern-dsr-tech-log-spin-icon" size={28} strokeWidth={2} />
          </span>
        )}
        tip="Loading EHS data..."
        size="large"
      >
        {isShiftSkipped ? (
          <div className="modern-dsr-tech-log-skipped-banner">
            <strong>
              {shift.charAt(0).toUpperCase() + shift.slice(1)} Shift Skipped
            </strong>
            <p>
              This section is disabled because the {shift} shift has been skipped.
            </p>
          </div>
        ) : (
          <div className="modern-dsr-tech-log-card">
            <div className="modern-dsr-tech-log-card-head modern-dsr-ehs-head">
              <div className="modern-dsr-tech-log-card-title-block">
                <div className="modern-dsr-tech-log-card-icon" aria-hidden>
                  <ShieldAlert size={22} strokeWidth={2} />
                </div>
                <div>
                  <h3 className="modern-dsr-tech-log-card-title">EHS issues and corrective actions</h3>
                  <p className="modern-dsr-tech-log-card-sub">Log health, safety, and environment issues; mark severity and note corrective actions per row.</p>
                </div>
              </div>
              <div className="modern-dsr-ehs-toolbar" role="group" aria-label="EHS row actions">
                <button
                  type="button"
                  className="modern-dsr-ehs-icon-btn"
                  onClick={() => setIsAddModalVisible(true)}
                  disabled={toolbarDisabled}
                  aria-label="Add issue"
                >
                  <Plus size={18} strokeWidth={2} />
                </button>
                <button
                  type="button"
                  className="modern-dsr-ehs-icon-btn modern-dsr-ehs-icon-btn--danger"
                  disabled={deleteDisabled}
                  onClick={handleDeleteSelected}
                  aria-label="Delete selected issues"
                >
                  <Trash2 size={18} strokeWidth={2} />
                </button>
              </div>
            </div>

            <div
                className="modern-dsr-tech-table modern-dsr-ehs-table-body"
                role="table"
                aria-label="EHS issues"
              >
                <div
                  className="modern-dsr-tech-thead modern-dsr-ehs-grid"
                  role="row"
                >
                  <div
                    className="modern-dsr-tech-th modern-dsr-ehs-th--tight"
                    aria-hidden
                  />
                  <div
                    className="modern-dsr-tech-th modern-dsr-ehs-th--tight"
                    role="columnheader"
                  >
                    Issues
                  </div>
                  <div
                    className="modern-dsr-tech-th modern-dsr-tech-th--center modern-dsr-ehs-th--tight"
                    role="columnheader"
                  >
                    Major
                  </div>
                  <div
                    className="modern-dsr-tech-th modern-dsr-tech-th--center modern-dsr-ehs-th--tight"
                    role="columnheader"
                  >
                    Minor
                  </div>
                  <div
                    className="modern-dsr-tech-th modern-dsr-ehs-th--tight"
                    role="columnheader"
                  >
                    Comments
                  </div>
                </div>

                {issues.length === 0 ? (
                  <div className="modern-dsr-tech-empty">No EHS issues loaded.</div>
                ) : (
                  issues.map((item) => (
                    <div
                      key={item.id}
                      className="modern-dsr-tech-row modern-dsr-ehs-grid"
                      role="row"
                      onMouseEnter={() => setHoveredRowId(item.id)}
                      onMouseLeave={() => setHoveredRowId(null)}
                      data-active={hoveredRowId === item.id ? 'true' : undefined}
                    >
                      <div
                        className="modern-dsr-tech-td modern-dsr-ehs-td--check"
                        role="cell"
                      >
                        <Checkbox
                          className="modern-dsr-ehs-check"
                          checked={isRowSelected(item.id)}
                          onChange={(e) => toggleRowSelected(item.id, e.target.checked)}
                          disabled={toolbarDisabled}
                        />
                      </div>
                      <div
                        className="modern-dsr-tech-td modern-dsr-ehs-td--issue"
                        role="cell"
                        title={item.name}
                      >
                        {item.name}
                      </div>
                      <div
                        className="modern-dsr-ehs-rg-merged"
                        style={{ gridColumn: '3 / 5' }}
                        role="cell"
                      >
                        <Radio.Group
                          className="modern-dsr-ehs-rg"
                          value={item.severity ?? undefined}
                          onChange={(e) =>
                            handleIssueChange(
                              item.id,
                              'severity',
                              e.target.value
                            )
                          }
                          disabled={skipped || isSubmitted}
                        >
                          <div className="modern-dsr-ehs-rg-inner">
                            <div className="modern-dsr-ehs-td--sev">
                              <Radio value="major">Major</Radio>
                            </div>
                            <div className="modern-dsr-ehs-td--sev">
                              <Radio value="minor">Minor</Radio>
                            </div>
                          </div>
                        </Radio.Group>
                      </div>
                      <div className="modern-dsr-tech-td" role="cell">
                        <Input
                          className="modern-dsr-ehs-text-input"
                          placeholder="Comments"
                          value={item.comments || ''}
                          onChange={(e) =>
                            handleIssueChange(
                              item.id,
                              'comments',
                              e.target.value
                            )
                          }
                          disabled={toolbarDisabled}
                        />
                      </div>
                    </div>
                  ))
                )}
              </div>
          </div>
        )}
      </Spin>

      <div
        className="modern-dsr-tech-log-form-footer"
        aria-label="Form actions"
      >
        <CrceButton
          label="Cancel"
          variant="tertiary"
          size="medium"
          onClick={onCancel}
        />
        <CrceButton
          label={isUpdatingEhsLog ? 'Saving…' : 'Save & Next'}
          variant="primary"
          size="medium"
          icon={Save}
          onClick={handleSave}
          disabled={isUpdatingEhsLog || isSubmitted || isShiftSkipped}
        />
      </div>

      <AddIssueModal
        visible={isAddModalVisible}
        onCancel={() => setIsAddModalVisible(false)}
        onOk={handleAddIssue}
        allIssues={allEhsIssues}
        issuesLoading={isAddingEhsLogItem}
      />
    </div>
  );
};

const mapStateToProps = ({ dsrReducer }) => ({
  currentEhsLog: dsrReducer.currentEhsLog,
  allEhsIssues: dsrReducer.allEhsIssues,
  isEhsLogLoading: dsrReducer.isEhsLogLoading,
  isUpdatingEhsLog: dsrReducer.isUpdatingEhsLog,
  isAddingEhsLogItem: dsrReducer.isAddingEhsLogItem,
  isDeletingEhsLogItem: dsrReducer.isDeletingEhsLogItem,
});

const mapDispatchToProps = {
  onFetchEhsLog: fetchEhsLog,
  onUpdateEhsLog: updateEhsLog,
  onAddEhsLogItem: addEhsLogItem,
  onDeleteEhsLogItem: deleteEhsLogItem,
  onFetchAllEhsIssues: fetchAllEhsIssues,
  onUpdateDsrLogSections: updateDsrLogSections,
};

export default connect(mapStateToProps, mapDispatchToProps)(EHSSection);
